/**
 * HTML sanitization utility. Whitelist-based: only allowed tags and
 * attributes survive, everything else is stripped. Used to safely
 * render dealerLabelHtml from solution configs.
 */

const ALLOWED_TAGS = new Set(["b", "i", "em", "strong", "span", "a"]); // dealer label formatting tags
// Removed entirely — text content is executable
const STRIP_ENTIRELY_TAGS = new Set([
  "script",
  "style",
  "iframe",
  "object",
  "embed"
]);
const ALLOWED_ATTRS = new Set(["class", "href", "target"]); // excludes all event handlers (on*) by omission
const JAVASCRIPT_PROTOCOL_RE = /^\s*javascript:/i; // catches mixed-case and leading whitespace

/**
 * Sanitizes an HTML string by whitelisting safe tags and attributes.
 * Parses with DOMParser, walks every node, strips disallowed content,
 * then serializes back to a string.
 *
 * @param input - raw HTML string to sanitize (may be null/undefined)
 * @returns sanitized HTML with only whitelisted tags, or empty string
 */
export function sanitizeHtml(input: string): string {
  if (input == null) {
    return "";
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(input, "text/html");
  sanitizeNode(doc.body);
  return doc.body.innerHTML;
}

/** Recursively sanitizes a DOM node and all descendants in place. */
function sanitizeNode(node: Element): void {
  const children = Array.from(node.children);

  for (const child of children) {
    sanitizeNode(child); // clean descendants first

    const tagName = child.tagName.toLowerCase();

    if (ALLOWED_TAGS.has(tagName)) {
      stripDisallowedAttributes(child);
    } else if (STRIP_ENTIRELY_TAGS.has(tagName)) {
      child.parentNode?.removeChild(child);
    } else {
      // Disallowed wrapper (e.g. <div>) — hoist children, remove element
      replaceWithChildren(child);
    }
  }
}

/** Removes non-whitelisted attributes; replaces javascript: hrefs with #. */
function stripDisallowedAttributes(element: Element): void {
  const attrNames = Array.from(element.attributes).map(attr => attr.name);

  for (const attrName of attrNames) {
    if (!ALLOWED_ATTRS.has(attrName)) {
      element.removeAttribute(attrName);
    } else if (attrName === "href") {
      const hrefValue = element.getAttribute("href") ?? "";
      if (JAVASCRIPT_PROTOCOL_RE.test(hrefValue)) {
        element.setAttribute("href", "#");
      }
    }
  }
}

/** Replaces a disallowed element with its children so text content survives. */
function replaceWithChildren(element: Element): void {
  const parent = element.parentNode;
  if (!parent) {
    return;
  }

  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element);
  }
  parent.removeChild(element);
}

/**
 * Strips markup and decodes entities, for use where a plain-text rendering of a
 * label is needed (e.g. a `title` attribute) rather than the sanitized HTML.
 */
export function htmlToText(input: string): string {
  if (input == null) {
    return "";
  }

  const parser = new DOMParser();
  return parser.parseFromString(input, "text/html").body.textContent ?? "";
}
